import * as Yup from 'yup';

/**
 * Allowed values of the Ancestry Review vehicle attributes the versioned symbol table (v2, #34372)
 * stamps on a car and the rater sends as vehicle_type_code / vehicle_horsepower_code /
 * vehicle_symbol_group (#35198). An underwriter entering symbols manually picks from these.
 */
export const vehicleTypeCodes = ['C', 'O', 'P', 'R', 'S', 'X'];
export const vehicleHorsepowerCodes = ['A', 'B', 'C', 'D', 'E', 'X'];
export const vehicleSymbolGroups = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
const toOptions = (codes) => codes.map((code) => ({ id: code, value: code }));
export const vehicleTypeCodeOptions = toOptions(vehicleTypeCodes);
export const vehicleHorsepowerCodeOptions = toOptions(vehicleHorsepowerCodes);
export const vehicleSymbolGroupOptions = toOptions(vehicleSymbolGroups);

const getCurrentCarInitialValue = (initialCars, vin) => {
  return (
    initialCars?.find((car) => car.VIN === vin) || {
      symbolMake: undefined,
      symbolAux: undefined,
      symbolStyle: undefined,
      symbolModel: undefined,
      symbolPGS: undefined,
      symbolBI: undefined,
      symbolPD: undefined,
      symbolMed: undefined,
      symbolPIP: undefined,
      symbolColl: undefined,
      symbolComp: undefined,
      symbolUM: undefined,
      vehicleTypeCode: undefined,
      vehicleHorsepowerCode: undefined,
      convertible: undefined,
      vehicleSymbolGroup: undefined
    }
  );
};

export const hasMakeModelStyleSymbols = (car) => {
  return car.symbolMake || car.symbolModel || car.symbolStyle;
};

/**
 * The per-coverage Verisk/RAPA symbols a farm-carrier auto (e.g. CFM) rates on (#36504), in GO's order.
 * `field` is the car's whole 2-char symbol for that coverage; `legacy` is the single-char pair the rater
 * falls back to when it is absent (liability for BI/PD/Med, physical damage for Comp/Coll); `syncsLegacy`
 * marks the coverage whose UW edit also rewrites that pair (see `rapaSymbolUpdates`).
 */
export const rapaCoverageSymbols = [
  {
    key: 'bodilyInjury',
    label: 'BI Symbol',
    field: 'rapaBodilyInjurySymbol',
    legacy: 'rapaLiabilitySymbol',
    syncsLegacy: true
  },
  { key: 'propertyDamage', label: 'PD Symbol', field: 'rapaPropertyDamageSymbol', legacy: 'rapaLiabilitySymbol' },
  { key: 'medPay', label: 'Med Symbol', field: 'rapaMedPaySymbol', legacy: 'rapaLiabilitySymbol' },
  { key: 'comprehensive', label: 'Comp Symbol', field: 'rapaComprehensiveSymbol', legacy: 'rapaPhysDamSymbol' },
  {
    key: 'collision',
    label: 'Coll Symbol',
    field: 'rapaCollisionSymbol',
    legacy: 'rapaPhysDamSymbol',
    syncsLegacy: true
  }
];

/**
 * A per-coverage RAPA symbol the rater will use: exactly two capital letters (the same check the CFM
 * vehicle answers handler applies). Anything else rates on the coverage's legacy pair instead. #36504
 */
export const isValidRapaSymbol = (value) => typeof value === 'string' && /^[A-Z]{2}$/.test(value);

/**
 * Yup fields for the five per-coverage symbols: a UW-entered symbol must be two letters before the form
 * saves. Only a changed value is checked (the stored car is matched by VIN), so an imported value already
 * on the record never blocks an unrelated edit. #36504
 */
export const rapaCoverageSymbolSchemaFields = () =>
  Object.fromEntries(
    rapaCoverageSymbols.map(({ field, label }) => [
      field,
      Yup.string()
        .nullable()
        .test(`${field}-two-letters`, `${label} must be two letters`, (value, { parent, options: { context } }) => {
          if (!value) {
            return true;
          }
          const storedCar = (context?.initialValues?.cars ?? []).find((car) => car.VIN === parent.VIN);
          return storedCar?.[field] === value || isValidRapaSymbol(value);
        })
    ])
  );

/**
 * The Verisk/RAPA rating symbols a farm-carrier auto (e.g. CFM) sends to ClarionDoor, one 2-char symbol
 * per coverage, formatted for display (#35042, #36504) — NOT the Branch make/model/style scheme. Returns
 * `{ bodilyInjury, propertyDamage, medPay, comprehensive, collision }`, or `null` when this is a Branch
 * auto, so callers can fall back to the make/model display.
 *
 * The values mirror exactly what the CFM auto rater sends (see packages/clarion-door-rating
 * .../carriers/cfm/raters/auto/answers-handlers/vehicle-answers.handler.ts): an antique always rates on
 * 'A' regardless of its stored symbols; otherwise each coverage's own symbol, else (a car symbolled
 * before #36504) its legacy pair, else the filed 'K' default Verisk prescribes for unsymbolled vehicles.
 *
 * @param {object} car
 * @param {{ usesRapaSymbols?: boolean }} [opts] `usesRapaSymbols` — the brand rates auto on RAPA
 *   symbols (a farm carrier such as CFM), so show the RAPA display even when the car stored none.
 */
export const getRapaRatingSymbols = (car, { usesRapaSymbols = false } = {}) => {
  const c = car ?? {};
  const hasAnyRapaSymbol = rapaCoverageSymbols.some(
    ({ field, legacy }) => c[field] || c[`${legacy}1`] || c[`${legacy}2`]
  );
  if (!usesRapaSymbols && !hasAnyRapaSymbol) {
    return null;
  }
  const half = (value) => (c.isAntique ? 'A' : value || 'K');
  return Object.fromEntries(
    rapaCoverageSymbols.map(({ key, field, legacy }) => {
      const own = isValidRapaSymbol(c[field]) ? c[field] : null;
      return [key, `${half(own?.[0] ?? c[`${legacy}1`])}${half(own?.[1] ?? c[`${legacy}2`])}`];
    })
  );
};

/**
 * The form updates for a UW-entered per-coverage symbol (#36504): the symbol upper-cased, plus — for
 * BI and Coll — the legacy single-char pair it stands for. The rating plan keys only on the legacy pairs
 * until it reads the per-coverage questions, so without the sync a UW edit would not move the rate;
 * BI → liability and Coll → physical damage is the convention a Verisk pull already follows. The pair is
 * only written once the symbol is two letters, so mid-typing or a typo never leaves a half, blank or
 * invalid pair to be rated.
 */
export const rapaSymbolUpdates = (field, value) => {
  const symbol = (value || '').toUpperCase();
  const coverage = rapaCoverageSymbols.find((c) => c.field === field);
  if (!coverage?.syncsLegacy || !isValidRapaSymbol(symbol)) {
    return { [field]: symbol };
  }
  return { [field]: symbol, [`${coverage.legacy}1`]: symbol[0], [`${coverage.legacy}2`]: symbol[1] };
};

const normalizedSymbol = (value) => value?.toUpperCase() || undefined;

export const haveAnyOfThisCarsSymbolsChanged = (currentCar, initialCars) => {
  const initialCarValue = getCurrentCarInitialValue(initialCars, currentCar.VIN);
  return (
    initialCarValue.symbolMake !== (currentCar.symbolMake?.toUpperCase() || undefined) ||
    initialCarValue.symbolModel !== (currentCar.symbolModel?.toUpperCase() || undefined) ||
    initialCarValue.symbolStyle !== (currentCar.symbolStyle?.toUpperCase() || undefined) ||
    initialCarValue.symbolAux !== (currentCar.symbolAux?.toUpperCase() || undefined) ||
    initialCarValue.symbolPGS !== (currentCar.symbolPGS?.toUpperCase() || undefined) ||
    initialCarValue.symbolBI !== currentCar.symbolBI ||
    initialCarValue.symbolPD !== currentCar.symbolPD ||
    initialCarValue.symbolMed !== currentCar.symbolMed ||
    initialCarValue.symbolPIP !== currentCar.symbolPIP ||
    initialCarValue.symbolColl !== currentCar.symbolColl ||
    initialCarValue.symbolComp !== currentCar.symbolComp ||
    initialCarValue.symbolUM !== currentCar.symbolUM ||
    // Ancestry Review attributes (#35198). The initial car comes from GraphQL, where an absent
    // attribute (a v1-symboled car) is null, so normalize both sides or an untouched car reads as changed.
    normalizedSymbol(initialCarValue.vehicleTypeCode) !== normalizedSymbol(currentCar.vehicleTypeCode) ||
    normalizedSymbol(initialCarValue.vehicleHorsepowerCode) !== normalizedSymbol(currentCar.vehicleHorsepowerCode) ||
    !!initialCarValue.convertible !== !!currentCar.convertible ||
    normalizedSymbol(initialCarValue.vehicleSymbolGroup) !== normalizedSymbol(currentCar.vehicleSymbolGroup)
  );
};
